import Footnote from '@allesauto/core/footnote';
import InfoIconBox from '@allesauto/core/infoIconBox';
import { useConsumption } from '@allesauto/core/useConsumption';
import { useConsumptionEmissionFootnote } from '@allesauto/core/useConsumptionEmissionFootnote';
import { useEmission } from '@allesauto/core/useEmission';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { FuelType } from '@allesauto/utils/consts';
import clsx from 'clsx';
import { Maybe } from 'graphql/jsutils/Maybe';
import Markdown from 'markdown-to-jsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import { markdownProps } from '@/utils/markdown';

import EfficiencyIcon, { EfficiencyType } from './EfficiencyIcon';

export interface EnvironmentEmissionProps {
  environment?: SchemaTypes.Environment | null;
  fuelType?: FuelType;
  isPrivateSeller?: boolean | null;
  emissionClassDetail: Maybe<string> | undefined;
  electricConsumption: Maybe<number> | undefined;
  consumption: Maybe<number> | undefined;
  hasConfiguratorDealer: boolean;
  co2Class?: Maybe<string>;
  co2ClassDischargedBatteryWltp?: Maybe<string>;
}

const EnvironmentEmission: FC<
  React.PropsWithChildren<EnvironmentEmissionProps>
> = ({
  environment,
  fuelType,
  isPrivateSeller,
  emissionClassDetail,
  electricConsumption,
  consumption,
  hasConfiguratorDealer,
  co2Class,
  co2ClassDischargedBatteryWltp,
}) => {
  const {
    emissionClass,
    co2Emission,
    fuelConsumptionExtraUrban,
    fuelConsumptionUrban,
    emissionSticker,
    wltEmission,
    wltConsumption,
    wltConsumptionElectric,
  } = environment ?? {};

  const emissionValue = useEmission(wltEmission!, co2Emission!);
  const { consumptionValue, wltpAvailable } = useConsumption({
    fuelType,
    electricConsumption,
    consumption,
    wltEmission,
    wltConsumption,
    wltConsumptionElectric,
    hasConfiguratorDealer,
  });

  const { footnote } = useConsumptionEmissionFootnote({
    emissionValue,
    consumptionValue,
    wltpAvailable,
  });

  function formatEmissionClassDetail(input: string) {
    return input.replace(/EURO(\d+)([A-Z_]+)/, (_, digits, suffix) => {
      const formattedSuffix = suffix
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (match: string) => match.toUpperCase());
      return `Euro ${digits}${formattedSuffix}`;
    });
  }

  const intl = useIntl();

  const co2EmissionsCombinedAdditionalInfo = translate(
    intl,
    'ui.PDP.ENVIRONMENT.CO2_EMISSIONS_COMBINED_ADDITIONAL_INFO'
  );

  const emissionClassAdditionalInfo = translate(
    intl,
    'ui.PDP.ENVIRONMENT.EMISSION_CLASS_ADDITIONAL_INFO'
  );

  const fuelConsumptionAdditionalInfo = translate(
    intl,
    'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_ADDITIONAL_INFO',
    {
      value1:
        typeof fuelConsumptionUrban === 'number'
          ? parseFloat(intl.formatNumber(fuelConsumptionUrban)).toString()
          : '-',
      value2:
        typeof fuelConsumptionExtraUrban === 'number'
          ? parseFloat(intl.formatNumber(fuelConsumptionExtraUrban)).toString()
          : '-',
    }
  );

  const baseColClass =
    'col-end-23 col-start-1 flex flex-col pt-30px md:pt-0 pb-30px md:pb-40px w-full';

  const className = {
    container: 'grid grid-cols-22 col-start-2 col-end-24 md:py-60px py-20px',
    headline:
      'col-end-23 col-start-1 font-weight-bold font-headline mb-30px md:mb-40px text-grey100 text-headline3',
    firstCol: clsx('md:col-end-9 md:col-start-1', baseColClass),
    secondCol: clsx('pt-30px md:col-end-15 md:col-start-9 ', baseColClass),
    thirdCol: clsx('pt-30px md:col-end-23 md:col-start-17 ', baseColClass),
    labelText:
      'font-regular text-grey100 text-p1 mb-5px flex items-center pt-30px ',
    valueText:
      'font-headline font-weight-bold text-headline4 text-purple80 monochrome:text-black',
    colSecondRow: 'mt-15px md:mt-25px',
    icon: 'ml-7px',
  };

  // both new and used
  const co2EmissionsContent = (
    <>
      <h3 className={className.labelText} id="label--co2-emission-combined">
        {translate(intl, 'ui.PDP.ENVIRONMENT.CO2_EMISSIONS_COMBINED')}
        {co2EmissionsCombinedAdditionalInfo && (
          <InfoIconBox
            text={co2EmissionsCombinedAdditionalInfo}
            customClassName={className.icon}
          />
        )}
      </h3>
      <div
        className={className.valueText}
        id="label--co2-emission-combined-value"
      >
        {emissionValue}
      </div>
    </>
  );

  // both new and used
  const fuelConsumptionContent = (
    <>
      <h3 className={className.labelText} id="label--fuel-consumption-combined">
        {fuelType === FuelType.ELECTRIC
          ? translate(
              intl,
              'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_COMBINED_ELECTRO'
            )
          : translate(intl, 'ui.PDP.ENVIRONMENT.FUEL_CONSUMPTION_COMBINED')}
        <InfoIconBox
          text={fuelConsumptionAdditionalInfo}
          customClassName={className.icon}
        />
      </h3>
      <div
        className={className.valueText}
        id="label--fuel-consumption-combined-value"
      >
        {consumptionValue}
      </div>
    </>
  );

  // both new and used
  const emissionClassContent = (
    <>
      <h3 className={className.labelText} id="label--emission-class">
        {translate(intl, 'ui.PDP.ENVIRONMENT.EMISSION_CLASS')}
        {emissionClassAdditionalInfo && (
          <InfoIconBox
            text={emissionClassAdditionalInfo}
            customClassName={className.icon}
          />
        )}
      </h3>
      <div
        className={`${className.valueText} capitalize`}
        id="label--emission-class-value"
      >
        {emissionClassDetail
          ? formatEmissionClassDetail(emissionClassDetail)
          : emissionClass ?? '-'}
      </div>
    </>
  );

  // only new cars
  const emissionStickerContent = (
    <>
      <h3 className={className.labelText} id="label--environment-badge">
        {translate(intl, 'ui.PDP.ENVIRONMENT.ENVIRONMENTAL_BADGE')}
      </h3>
      <div
        className={`${className.valueText} capitalize`}
        id="label--environment-badge-value"
      >
        {emissionSticker ?? '-'}
      </div>
    </>
  );

  return (
    <div className={className.container} data-style-for-print="environment">
      <h2 className={className.headline} id="label--title-environment">
        {translate(intl, 'ui.PDP.ENVIRONMENT.HEADLINE')}
      </h2>

      <div className={className.firstCol}>
        {isPrivateSeller ? (
          co2EmissionsContent
        ) : wltpAvailable && co2Class && fuelType !== FuelType.HYBRID ? (
          <>
            <div className="flex flex-auto">
              <EfficiencyIcon efficiencyCo2Class={co2Class as EfficiencyType} />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-auto">
              <EfficiencyIcon
                efficiencyCo2Class={co2Class as EfficiencyType}
                co2ClassDischargedBatteryWltp={
                  co2ClassDischargedBatteryWltp as EfficiencyType
                }
              />
            </div>
          </>
        )}
      </div>

      <div className={className.secondCol}>
        {isPrivateSeller ? (
          fuelConsumptionContent
        ) : (
          <>
            <div>{co2EmissionsContent}</div>
            <div className={className.colSecondRow}>
              {fuelConsumptionContent}
            </div>
          </>
        )}
      </div>

      <div className={className.thirdCol}>
        {isPrivateSeller ? (
          emissionClassContent
        ) : (
          <>
            <div>{emissionStickerContent}</div>
            <div className={className.colSecondRow}>{emissionClassContent}</div>
          </>
        )}
      </div>

      {!isPrivateSeller && (
        <div className={clsx('col-end-23 col-start-1 md:col-end-16')}>
          <Footnote
            items={[
              {
                content: <Markdown {...markdownProps}>{footnote}</Markdown>,
                type: 'emission',
              },
            ]}
            showIdentifier={false}
          />
        </div>
      )}
    </div>
  );
};

export default EnvironmentEmission;
